import type { FocusEvent, ReactNode } from 'react';
import { useCallback, useId } from 'react';

import { useFormContext } from './form-context';
import { HintText } from './hint-text';
import { Label } from './input/label';

// =============================================================================
// Types
// =============================================================================

export interface FormFieldRenderProps {
  onBlur: () => void;
  error: string | undefined;
  showError: boolean;
  isInvalid: boolean;
  /** Unique ID for the input element — use as `id` to associate with the label */
  inputId: string;
  /**
   * ID of the `<label>` THIS FormField rendered, or `undefined` when it rendered
   * none (no `label` prop). Pass it straight through as `aria-labelledby` on the
   * control — it is safe to pass unconditionally.
   *
   * Needed because `lib/base/forms/input` falls back to
   * `aria-label={placeholder}` whenever it has no `label` prop of its own — and
   * an explicit `aria-label` OVERRIDES an associated `<label for>` in the
   * accessible-name computation. So a FormField-wrapped Input announces its
   * placeholder ("Ada") instead of its label ("First name"). Passing
   * `aria-labelledby={labelId}` suppresses that fallback and restores the label
   * as the accessible name.
   *
   * It is deliberately `undefined` rather than always-a-string: some callers
   * render their own `<label>` inside the render prop and pass no `label` to
   * FormField (see pages/auth/LoginContent.tsx). Pointing `aria-labelledby` at
   * an element that does not exist yields an EMPTY accessible name — worse than
   * the placeholder fallback it was meant to replace.
   */
  labelId: string | undefined;
}

export interface FormFieldProps {
  /** Field name — must match a key in the FormProvider's fields config */
  name: string;
  /** Label text */
  label?: string | ReactNode;
  /** Hint text shown below the field when no error is visible */
  hint?: ReactNode;
  /** Whether to show the required indicator (*) on the label */
  isRequired?: boolean;
  /** Hide the required indicator even if isRequired is true */
  hideRequiredIndicator?: boolean;
  /** Additional className for the wrapper */
  className?: string;
  /** id to apply to the rendered error text — pass this as aria-describedby on the field's control(s) to associate the error programmatically */
  errorId?: string;
  /** Children: render prop for full control, or ReactNode for simple wrapping */
  children: ReactNode | ((props: FormFieldRenderProps) => ReactNode);
}

// =============================================================================
// FormField
// =============================================================================

export function FormField({
  name,
  label,
  hint,
  isRequired,
  hideRequiredIndicator,
  className,
  errorId,
  children,
}: FormFieldProps) {
  const inputId = useId();
  const labelId = useId();
  const { getFieldProps } = useFormContext();
  const { onBlur, error, showError } = getFieldProps(name);

  // Capture blur events from any descendant input and decide whether to mark
  // the field touched. Two reasons we DON'T mark touched:
  //
  // (1) Focus moved within the same FormField (e.g. input → trailing button
  //     inside an InputGroup) — this isn't the user leaving the field.
  //
  // (2) Focus moved outside the parent <form> entirely (e.g. clicking an OAuth
  //     provider button or navigation link rendered outside the form). The user
  //     is abandoning the form, not attempting to submit it, so showing
  //     "Required" on autoFocused fields they never typed in is just noise.
  //     React-aria's <Button> programmatically focuses on press (Safari quirk)
  //     which bypasses mousedown/pointerdown preventDefault on wrappers, so a
  //     relatedTarget check here is the only reliable guard at this layer.
  //
  // We compare closest('form') references rather than using form.contains()
  // because happy-dom (our test environment) has bugs with Element.contains()
  // for cross-subtree checks; closest comparison is also semantically clearer
  // ("are both elements inside the same form?").
  const handleBlurCapture = useCallback(
    (e: FocusEvent) => {
      const related = e.relatedTarget as HTMLElement | null;

      if (related && e.currentTarget.contains(related)) {
        return;
      }

      const ownForm = (e.currentTarget as HTMLElement).closest('form');
      if (ownForm) {
        const relatedForm = related?.closest('form') ?? null;
        if (relatedForm !== ownForm) {
          return;
        }
      }

      onBlur();
    },
    [onBlur]
  );

  const renderProps: FormFieldRenderProps = {
    onBlur,
    error,
    showError,
    isInvalid: showError && !!error,
    inputId,
    // Only report the id when the <label> carrying it is actually rendered.
    labelId: label ? labelId : undefined,
  };

  const isRenderProp = typeof children === 'function';

  return (
    <div
      data-form-field={name}
      onBlurCapture={handleBlurCapture}
      className={className}
    >
      {label && (
        <Label
          id={labelId}
          htmlFor={inputId}
          isRequired={hideRequiredIndicator ? false : isRequired}
          className='mb-1.5'
        >
          {label}
        </Label>
      )}

      {isRenderProp ? children(renderProps) : children}

      {showError && error && (
        <HintText id={errorId} isInvalid showError className='mt-1.5'>
          {error}
        </HintText>
      )}
      {!(showError && error) && hint && (
        <HintText className='mt-1.5'>{hint}</HintText>
      )}
    </div>
  );
}
